import type { ICellRendererParams } from 'ag-grid-community';
import { compact, isNil } from 'lodash';
import React from 'react';
import type { Portfolio, PortfolioNodeDesignationEnum } from '../../../../../../venn-api/src';
import { TableRowOptionsMenu } from '../../../../../../venn-ui-kit/src';
import type { RowData } from '../../types';
import { useIsLeverageShortEnabled } from '../../../../hooks/leverageAndShort';
import { CASH_BORROWING, CASH_HOLDING } from '../review/helpers';
import { Match } from 'effect';

type MutliPortfolioActionsCellProps = ICellRendererParams<RowData<Portfolio>> &
  Readonly<{
    isHistorical: boolean;
    isExcluded: boolean;
    includeInvestment: () => void;
    excludeInvestment: () => void;
    changeInvestmentDesignation: (path: number[], designation: PortfolioNodeDesignationEnum) => void;
  }>;

const getCashDesignationOptionLabel = (isCashNode: boolean, isNegative: boolean) => {
  const cashHoldingOrBorrowing = isNegative ? CASH_BORROWING : CASH_HOLDING;
  if (!isCashNode) {
    return `Designate as ${cashHoldingOrBorrowing}`;
  }
  return `Remove ${cashHoldingOrBorrowing} Designation`;
};

const getLabelForAction = ({
  isExcluded,
  itemType,
}: {
  isExcluded: boolean;
  itemType: 'historical' | 'proforma' | 'cash';
}) =>
  Match.value([isExcluded, itemType]).pipe(
    Match.when([true, 'cash'], () => 'Re-include cash position in upload'),
    Match.when([true, Match.any], () => 'Re-include investment in upload'),
    Match.when([false, 'historical'], () => 'Exclude investment from all dates'),
    Match.when([false, 'proforma'], () => 'Exclude investment from upload'),
    Match.when([false, 'cash'], () => 'Exclude cash position from upload'),
    Match.exhaustive,
  );

const MutliPortfolioActionsCell = ({
  data,
  isHistorical,
  isExcluded,
  includeInvestment,
  excludeInvestment,
  changeInvestmentDesignation,
}: MutliPortfolioActionsCellProps) => {
  const isLeverageShortEnabled = useIsLeverageShortEnabled();
  if (isNil(data?.node?.fund) || isNil(data?.path) || data?.isLotRow) {
    return null;
  }

  const isCashNode = data.node.designation === 'CASH';
  return (
    <TableRowOptionsMenu
      usePortal
      portalProps={{
        topOffset: 20,
        rightOffset: 210,
      }}
      options={compact([
        {
          label: getLabelForAction({
            isExcluded,
            itemType: isCashNode ? 'cash' : isHistorical ? 'historical' : 'proforma',
          }),
          destructive: !isExcluded,
          onClick: () => {
            if (isExcluded) {
              includeInvestment();
            } else {
              excludeInvestment();
            }
          },
        },
        isLeverageShortEnabled &&
          !isHistorical && {
            label: getCashDesignationOptionLabel(isCashNode, (data.node.netAssets ?? 0) < 0),
            destructive: isCashNode,
            onClick: () => {
              if (isCashNode) {
                changeInvestmentDesignation(data?.path, 'DEFAULT');
              } else {
                changeInvestmentDesignation(data?.path, 'CASH');
              }
            },
          },
      ])}
    />
  );
};

export default MutliPortfolioActionsCell;
